// pages/EinkDesigner/index.jsx
// E-Ink Display Designer — visual editor for Waveshare 2.7" e-paper displays (264×176)
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, IconButton, TextField, Select, MenuItem,
  FormControl, InputLabel, Checkbox, FormControlLabel, Paper, Divider,
  Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Slider,
  Chip, Stack, CircularProgress, Alert, Tabs, Tab, Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, Save as SaveIcon,
  Publish as PushIcon, TextFields as TextIcon, CropSquare as RectIcon,
  HorizontalRule as LineIcon, RadioButtonUnchecked as CircleIcon,
  Image as ImageIcon, DataObject as JsonIcon, Refresh as RefreshIcon,
  ArrowUpward, ArrowDownward, ContentCopy, Close as CloseIcon,
  Tune as TuneIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { einkDisplayApi, deviceTypesApi } from '../../services/apiClient';
import { useThemeSettings } from '../../contexts/ThemeContext';

// ─── Constants ─────────────────────────────────────────────────────
const DW = 264, DH = 176; // native display resolution
const SCALE = 3;           // zoom factor
const CW = DW * SCALE, CH = DH * SCALE;
const SNAP = 2;

// Predefined variables grouped by source service
const VAR_GROUPS = {
  time: [
    'time','time_s','date','date_us','year','month','month_name','day',
    'weekday','hour','minute','second',
  ],
  system: [
    'ip','wan_ip','hostname','wifi_db','wifi_ssid','cpu_temp','uptime',
    'mem_total','mem_used','mem_free','mac','cpu','mem','disk','internet',
  ],
  sensor: [
    'temperature','humidity',
  ],
  device: [
    'model','version','serial','cpu_arch','cpu_cores','memory_total',
  ],
  agent: [
    'device_uuid','agent_version','services_count','services_list','mqtt_host',
  ],
  sip: [
    'svc_sip_client_installed','svc_sip_client_status','sip_registered',
    'sip_extension','sip_server','sip_name','sip_call_active',
    'sip_calls_answered','sip_calls_auto','sip_calls_missed',
    'sip_running','sip_uptime','svc_sip_client_issues',
  ],
  services: [
    'svc_enocean_daemon_installed','svc_enocean_daemon_status',
    'svc_enocean_antenna_usb_installed','svc_enocean_antenna_usb_status',
  ],
  network: [
    'net_active_type','net_active_iface','net_active_ip',
    'net_eth_status','net_eth_ip','net_eth_speed','net_eth_mac',
    'net_wifi_status','net_wifi_ip','net_wifi_ssid','net_wifi_signal','net_wifi_mac',
    'net_cell_status','net_cell_ip','net_cell_signal','net_cell_operator','net_cell_type',
    'net_priority',
  ],
};

const BUILTIN_VARS = Object.values(VAR_GROUPS).flat();

const ICON_NAMES = [
  'wifi','battery','check','cross','heart','star','clock','mail',
  'home','gear','sun','cloud','warning','signal','bell','power',
  'lightning','arrow-up','arrow-down','ethernet','cellular',
];

// ─── Icon Drawing Functions (matches display.py exactly) ──────────
const ICON_DRAW = {
  wifi(ctx, s) {
    const cx = s/2, cy = s - 2;
    ctx.lineWidth = Math.max(1, s/10);
    [0.9, 0.6, 0.3].forEach(f => {
      ctx.beginPath();
      ctx.arc(cx, cy, s*f, 210*Math.PI/180, 330*Math.PI/180);
      ctx.stroke();
    });
    ctx.beginPath(); ctx.arc(cx, cy, Math.max(1, s/8), 0, Math.PI*2); ctx.fill();
  },
  battery(ctx, s) {
    const bw = s*0.8, bh = s*0.5, by = (s-bh)/2;
    ctx.lineWidth = Math.max(1, s/12);
    ctx.strokeRect(0, by, bw, bh);
    ctx.fillRect(bw, by+(bh-bh/3)/2, 3, bh/3);
    ctx.fillRect(3, by+3, bw*0.6, bh-6);
  },
  check(ctx, s) {
    ctx.lineWidth = Math.max(2, s/8);
    ctx.beginPath();
    ctx.moveTo(s*0.15, s*0.5); ctx.lineTo(s*0.4, s*0.75); ctx.lineTo(s*0.85, s*0.2);
    ctx.stroke();
  },
  cross(ctx, s) {
    ctx.lineWidth = Math.max(2, s/8);
    const m = s*0.2;
    ctx.beginPath(); ctx.moveTo(m, m); ctx.lineTo(s-m, s-m); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(s-m, m); ctx.lineTo(m, s-m); ctx.stroke();
  },
  heart(ctx, s) {
    ctx.beginPath();
    for (let t = 0; t <= 100; t++) {
      const a = Math.PI*2*t/100;
      const px = 16*Math.pow(Math.sin(a), 3);
      const py = -(13*Math.cos(a)-5*Math.cos(2*a)-2*Math.cos(3*a)-Math.cos(4*a));
      const x = s/2+px*s/36, y = s/2+py*s/36-s*0.1;
      t === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    }
    ctx.closePath(); ctx.fill();
  },
  star(ctx, s) {
    const cx = s/2, cy = s/2, r = s*0.45;
    ctx.beginPath();
    for (let i = 0; i < 10; i++) {
      const a = Math.PI/2 + Math.PI*2*i/10;
      const cr = i%2 === 0 ? r : r*0.4;
      const x = cx+cr*Math.cos(a), y = cy-cr*Math.sin(a);
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    }
    ctx.closePath(); ctx.fill();
  },
  clock(ctx, s) {
    const cx = s/2, cy = s/2, r = s*0.42;
    ctx.lineWidth = Math.max(1, s/12);
    ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI*2); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(cx, cy); ctx.lineTo(cx, cy-r*0.65); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(cx, cy); ctx.lineTo(cx+r*0.5, cy+r*0.2); ctx.stroke();
  },
  mail(ctx, s) {
    ctx.lineWidth = Math.max(1, s/12);
    const m = s*0.1, ey = s*0.25;
    ctx.strokeRect(m, ey, s-2*m, s-2*ey);
    ctx.beginPath(); ctx.moveTo(m, ey); ctx.lineTo(s/2, s*0.55); ctx.lineTo(s-m, ey); ctx.stroke();
  },
  home(ctx, s) {
    ctx.lineWidth = Math.max(1, s/10);
    const cx = s/2;
    ctx.beginPath();
    ctx.moveTo(cx, s*0.1); ctx.lineTo(s*0.1, s*0.5); ctx.lineTo(s*0.9, s*0.5); ctx.closePath();
    ctx.stroke();
    ctx.strokeRect(s*0.25, s*0.5, s*0.5, s*0.4);
    ctx.fillRect(cx-s*0.12, s*0.6, s*0.24, s*0.3);
  },
  gear(ctx, s) {
    const cx = s/2, cy = s/2, r1 = s*0.4, r2 = s*0.25;
    ctx.lineWidth = Math.max(1, s/10);
    ctx.beginPath(); ctx.arc(cx, cy, r2, 0, Math.PI*2); ctx.stroke();
    ctx.lineWidth = Math.max(2, s*0.12);
    for (let i = 0; i < 8; i++) {
      const a = Math.PI*2*i/8;
      ctx.beginPath();
      ctx.moveTo(cx+r2*Math.cos(a), cy+r2*Math.sin(a));
      ctx.lineTo(cx+r1*Math.cos(a), cy+r1*Math.sin(a));
      ctx.stroke();
    }
  },
  sun(ctx, s) {
    const cx = s/2, cy = s/2, r = s*0.2;
    ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI*2); ctx.fill();
    ctx.lineWidth = Math.max(1, s/12);
    for (let i = 0; i < 8; i++) {
      const a = Math.PI*2*i/8;
      ctx.beginPath();
      ctx.moveTo(cx+r*1.4*Math.cos(a), cy+r*1.4*Math.sin(a));
      ctx.lineTo(cx+r*2.2*Math.cos(a), cy+r*2.2*Math.sin(a));
      ctx.stroke();
    }
  },
  cloud(ctx, s) {
    const fe = (x1,y1,x2,y2) => {
      ctx.beginPath();
      ctx.ellipse((x1+x2)/2,(y1+y2)/2,(x2-x1)/2,(y2-y1)/2,0,0,Math.PI*2);
      ctx.fill();
    };
    fe(s*0.1,s*0.3,s*0.5,s*0.7);
    fe(s*0.3,s*0.15,s*0.7,s*0.6);
    fe(s*0.5,s*0.25,s*0.9,s*0.7);
    ctx.fillRect(s*0.15, s*0.5, s*0.7, s*0.2);
  },
  warning(ctx, s) {
    ctx.lineWidth = Math.max(2, s/8);
    const cx = s/2;
    ctx.beginPath();
    ctx.moveTo(cx, s*0.08); ctx.lineTo(s*0.08, s*0.88); ctx.lineTo(s*0.92, s*0.88); ctx.closePath();
    ctx.stroke();
    ctx.beginPath(); ctx.moveTo(cx, s*0.35); ctx.lineTo(cx, s*0.6); ctx.stroke();
    ctx.beginPath(); ctx.arc(cx, s*0.7, Math.max(1, s*0.04), 0, Math.PI*2); ctx.fill();
  },
  signal(ctx, s) {
    const bars = 4, bw = s*0.15;
    const gap = (s - bars*bw) / (bars+1);
    for (let i = 0; i < bars; i++) {
      const bh = s*0.3 + s*0.6*(i+1)/bars;
      ctx.fillRect(gap + i*(bw+gap), s-bh, bw, bh);
    }
  },
  bell(ctx, s) {
    const cx = s/2;
    ctx.lineWidth = Math.max(1, s/12);
    ctx.beginPath();
    ctx.ellipse(s*0.5, (s*0.1+s*0.65)/2, (s*0.8-s*0.2)/2, (s*0.65-s*0.1)/2, 0, Math.PI, 0);
    ctx.stroke();
    ctx.beginPath(); ctx.moveTo(s*0.2, s*0.55); ctx.lineTo(s*0.2, s*0.72); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(s*0.8, s*0.55); ctx.lineTo(s*0.8, s*0.72); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(s*0.12, s*0.72); ctx.lineTo(s*0.88, s*0.72); ctx.stroke();
    ctx.beginPath();
    ctx.ellipse(cx, (s*0.78+s*0.92)/2, s*0.07, (s*0.92-s*0.78)/2, 0, 0, Math.PI*2);
    ctx.fill();
  },
  power(ctx, s) {
    const cx = s/2, cy = s/2, r = s*0.38;
    ctx.lineWidth = Math.max(2, s/8);
    ctx.beginPath(); ctx.arc(cx, cy, r, 40*Math.PI/180, 320*Math.PI/180); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(cx, s*0.12); ctx.lineTo(cx, cy); ctx.stroke();
  },
  lightning(ctx, s) {
    const pts = [[s*0.55,s*0.05],[s*0.2,s*0.5],[s*0.48,s*0.5],[s*0.35,s*0.95],[s*0.8,s*0.4],[s*0.52,s*0.4]];
    ctx.beginPath(); ctx.moveTo(pts[0][0], pts[0][1]);
    for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0], pts[i][1]);
    ctx.closePath(); ctx.fill();
  },
  'arrow-up'(ctx, s) {
    const cx = s/2;
    ctx.lineWidth = Math.max(2, s/8);
    ctx.beginPath(); ctx.moveTo(cx, s*0.15); ctx.lineTo(cx, s*0.85); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(s*0.25, s*0.4); ctx.lineTo(cx, s*0.15); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(s*0.75, s*0.4); ctx.lineTo(cx, s*0.15); ctx.stroke();
  },
  'arrow-down'(ctx, s) {
    const cx = s/2;
    ctx.lineWidth = Math.max(2, s/8);
    ctx.beginPath(); ctx.moveTo(cx, s*0.15); ctx.lineTo(cx, s*0.85); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(s*0.25, s*0.6); ctx.lineTo(cx, s*0.85); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(s*0.75, s*0.6); ctx.lineTo(cx, s*0.85); ctx.stroke();
  },
  ethernet(ctx, s) {
    const lw = Math.max(1, s/12);
    ctx.lineWidth = lw;
    // Plug body
    ctx.strokeRect(s*0.2, s*0.15, s*0.6, s*0.55);
    // Clip tab
    ctx.strokeRect(s*0.35, s*0.05, s*0.3, s*0.1);
    // Cable
    ctx.beginPath(); ctx.moveTo(s/2, s*0.7); ctx.lineTo(s/2, s*0.95); ctx.stroke();
    // Contact pins
    ctx.lineWidth = Math.max(1, lw - 1);
    for (const px of [0.32, 0.44, 0.56, 0.68]) {
      ctx.beginPath(); ctx.moveTo(s*px, s*0.3); ctx.lineTo(s*px, s*0.5); ctx.stroke();
    }
  },
  cellular(ctx, s) {
    const bw = s * 0.15, gap = s * 0.05, baseY = s * 0.85;
    for (let i = 0; i < 4; i++) {
      const bx = s*0.1 + i * (bw + gap);
      const bh = s * 0.2 * (i + 1);
      ctx.fillRect(bx, baseY - bh, bw, bh);
    }
  },
};

// ─── Utility Functions ────────────────────────────────────────────
const iconCache = {};
function renderIconToDataURL(name, size) {
  const key = `${name}_${size}`;
  if (iconCache[key]) return iconCache[key];
  const c = document.createElement('canvas');
  c.width = size; c.height = size;
  const ctx = c.getContext('2d');
  ctx.fillStyle = '#000'; ctx.strokeStyle = '#000';
  const fn = ICON_DRAW[name];
  if (fn) fn(ctx, size);
  iconCache[key] = c.toDataURL();
  return iconCache[key];
}

function iconToPixels(name, size) {
  const c = document.createElement('canvas');
  c.width = size; c.height = size;
  const ctx = c.getContext('2d');
  ctx.fillStyle = '#000'; ctx.strokeStyle = '#000';
  const fn = ICON_DRAW[name];
  if (fn) fn(ctx, size);
  const imgData = ctx.getImageData(0, 0, size, size);
  const rows = [];
  for (let y = 0; y < size; y++) {
    let row = '';
    for (let x = 0; x < size; x++) {
      const i = (y * size + x) * 4;
      const lum = imgData.data[i]*0.299 + imgData.data[i+1]*0.587 + imgData.data[i+2]*0.114;
      row += lum < 128 ? '1' : '0';
    }
    rows.push(row);
  }
  return rows;
}

function stepDownScale(img, targetW, targetH) {
  let sw = img.naturalWidth || img.width, sh = img.naturalHeight || img.height;
  let src = img;
  // Step-down halving for large scale ratios (preserves detail)
  while (sw > targetW * 2 || sh > targetH * 2) {
    const hw = Math.max(targetW, Math.ceil(sw / 2));
    const hh = Math.max(targetH, Math.ceil(sh / 2));
    const tc = document.createElement('canvas');
    tc.width = hw; tc.height = hh;
    const tctx = tc.getContext('2d');
    tctx.imageSmoothingEnabled = true;
    tctx.imageSmoothingQuality = 'high';
    tctx.drawImage(src, 0, 0, hw, hh);
    src = tc; sw = hw; sh = hh;
  }
  const fc = document.createElement('canvas');
  fc.width = targetW; fc.height = targetH;
  const fctx = fc.getContext('2d');
  fctx.imageSmoothingEnabled = true;
  fctx.imageSmoothingQuality = 'high';
  fctx.fillStyle = '#fff';
  fctx.fillRect(0, 0, targetW, targetH);
  fctx.drawImage(src, 0, 0, targetW, targetH);
  return fctx.getImageData(0, 0, targetW, targetH);
}

// Convert image to 1-bit pixel rows with adjustable parameters
// opts: { threshold, contrast, brightness, dithering, inverted }
function imageToPixels(dataUrl, width, height, inverted, opts = {}) {
  const threshold = opts.threshold ?? 128;
  const contrast = opts.contrast ?? 0;    // -100 to 100
  const brightness = opts.brightness ?? 0; // -100 to 100
  const dithering = opts.dithering !== false;
  return new Promise(resolve => {
    const img = new Image();
    img.onload = () => {
      const imgData = stepDownScale(img, width, height);
      // Build grayscale buffer with contrast/brightness adjustments
      const gray = new Float32Array(width * height);
      const cFactor = (259 * (contrast + 255)) / (255 * (259 - contrast));
      for (let i = 0; i < gray.length; i++) {
        const p = i * 4;
        let v = imgData.data[p]*0.299 + imgData.data[p+1]*0.587 + imgData.data[p+2]*0.114;
        // Apply brightness
        v += brightness * 2.55;
        // Apply contrast
        v = cFactor * (v - 128) + 128;
        gray[i] = Math.max(0, Math.min(255, v));
      }
      if (dithering) {
        // Floyd-Steinberg dithering
        for (let y = 0; y < height; y++) {
          for (let x = 0; x < width; x++) {
            const idx = y * width + x;
            const old = gray[idx];
            const nw = old < threshold ? 0 : 255;
            gray[idx] = nw;
            const err = old - nw;
            if (x + 1 < width)                  gray[idx + 1]         += err * 7 / 16;
            if (y + 1 < height && x > 0)        gray[idx + width - 1] += err * 3 / 16;
            if (y + 1 < height)                  gray[idx + width]     += err * 5 / 16;
            if (y + 1 < height && x + 1 < width) gray[idx + width + 1] += err * 1 / 16;
          }
        }
      } else {
        // Simple threshold (no dithering)
        for (let i = 0; i < gray.length; i++) {
          gray[i] = gray[i] < threshold ? 0 : 255;
        }
      }
      const rows = [];
      for (let y = 0; y < height; y++) {
        let row = '';
        for (let x = 0; x < width; x++) {
          let black = gray[y * width + x] < 128;
          if (inverted) black = !black;
          row += black ? '1' : '0';
        }
        rows.push(row);
      }
      resolve(rows);
    };
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });
}

// Render a B&W preview canvas from an image with given conversion settings
function renderBWPreview(img, width, height, opts = {}) {
  const threshold = opts.threshold ?? 128;
  const contrast = opts.contrast ?? 0;
  const brightness = opts.brightness ?? 0;
  const dithering = opts.dithering !== false;
  const inverted = opts.inverted || false;

  const imgData = stepDownScale(img, width, height);
  const gray = new Float32Array(width * height);
  const cFactor = (259 * (contrast + 255)) / (255 * (259 - contrast));
  for (let i = 0; i < gray.length; i++) {
    const p = i * 4;
    let v = imgData.data[p]*0.299 + imgData.data[p+1]*0.587 + imgData.data[p+2]*0.114;
    v += brightness * 2.55;
    v = cFactor * (v - 128) + 128;
    gray[i] = Math.max(0, Math.min(255, v));
  }
  if (dithering) {
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const idx = y * width + x;
        const old = gray[idx];
        const nw = old < threshold ? 0 : 255;
        gray[idx] = nw;
        const err = old - nw;
        if (x + 1 < width)                  gray[idx + 1]         += err * 7 / 16;
        if (y + 1 < height && x > 0)        gray[idx + width - 1] += err * 3 / 16;
        if (y + 1 < height)                  gray[idx + width]     += err * 5 / 16;
        if (y + 1 < height && x + 1 < width) gray[idx + width + 1] += err * 1 / 16;
      }
    }
  } else {
    for (let i = 0; i < gray.length; i++) gray[i] = gray[i] < threshold ? 0 : 255;
  }
  // Build ImageData for canvas display
  const out = new ImageData(width, height);
  for (let i = 0; i < gray.length; i++) {
    let bw = gray[i] < 128 ? 0 : 255;
    if (inverted) bw = 255 - bw;
    out.data[i*4] = out.data[i*4+1] = out.data[i*4+2] = bw;
    out.data[i*4+3] = 255;
  }
  return out;
}

function getLocalTimeVars() {
  const now = new Date();
  const pad = n => String(n).padStart(2, '0');
  const days = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return {
    time: pad(now.getHours()) + ':' + pad(now.getMinutes()),
    time_s: pad(now.getHours()) + ':' + pad(now.getMinutes()) + ':' + pad(now.getSeconds()),
    date: pad(now.getDate()) + '-' + pad(now.getMonth()+1) + '-' + now.getFullYear(),
    date_us: pad(now.getMonth()+1) + '/' + pad(now.getDate()) + '/' + now.getFullYear(),
    year: String(now.getFullYear()),
    month: pad(now.getMonth()+1),
    month_name: months[now.getMonth()],
    day: pad(now.getDate()),
    weekday: days[now.getDay()],
    hour: pad(now.getHours()),
    minute: pad(now.getMinutes()),
    second: pad(now.getSeconds()),
  };
}

function resolveVars(text, variables) {
  const timeVars = getLocalTimeVars();
  return text.replace(/\{\{(\w+)\}\}/g, (m, name) => {
    if (timeVars[name] !== undefined) return timeVars[name];
    return variables[name] !== undefined ? variables[name] : m;
  });
}

function detectVariables(pages) {
  const vars = new Set();
  for (const p of pages) {
    for (const el of p.elements) {
      if ((el.type === 'text' || el.type === 'json') && (el.text || el.data)) {
        const str = el.text || el.data || '';
        let match;
        const re = /\{\{(\w+)\}\}/g;
        while ((match = re.exec(str)) !== null) vars.add(match[1]);
      }
    }
  }
  return Array.from(vars).sort();
}

let nextId = 1;

// ─── Main Component ───────────────────────────────────────────────
export default function EinkDesigner() {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useThemeSettings();

  // Device type selection
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [loadingTypes, setLoadingTypes] = useState(true);

  // Designer state
  const [pages, setPages] = useState([{ name: 'Page 1', elements: [] }]);
  const [curPage, setCurPage] = useState(0);
  const [selId, setSelId] = useState(null);
  const [variables, setVariables] = useState({});
  const [version, setVersion] = useState(0);
  const [homeTimeout, setHomeTimeout] = useState(120);
  const [networkPriority, setNetworkPriority] = useState(['ethernet', 'wifi', 'cellular']);
  const [saving, setSaving] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Button config dialog
  const [buttonConfigPage, setButtonConfigPage] = useState(null);

  // Pixel editor
  const [pixelEditorOpen, setPixelEditorOpen] = useState(false);
  const [pixelEditorEl, setPixelEditorEl] = useState(null);
  const [pixelEditorInitPixels, setPixelEditorInitPixels] = useState(null);

  // Image preview dialog
  const [imgPreview, setImgPreview] = useState(null); // { dataUrl, width, height, mode: 'add'|'replace' }

  // Drag state
  const dragRef = useRef(null);
  const canvasRef = useRef(null);
  const overlayRef = useRef(null);
  const fileInputRef = useRef(null);
  const replaceInputRef = useRef(null);
  const imgCacheRef = useRef({});

  // Memoized helpers
  const curElements = useCallback(() => pages[curPage]?.elements || [], [pages, curPage]);
  const getEl = useCallback((id) => curElements().find(e => e.id === id), [curElements]);

  // ─── Load device types ─────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const res = await deviceTypesApi.getAll({ limit: 1000 });
        const allTypes = res.data?.deviceTypes || res.data?.data || res.data || [];
        setDeviceTypes(allTypes);
      } catch (err) {
        enqueueSnackbar(t('EinkDesigner.load_device_types_failed'), { variant: 'error' });
      } finally {
        setLoadingTypes(false);
      }
    })();
  }, []);

  // ─── Load template when device type changes ────────────────────
  useEffect(() => {
    if (!selectedType) return;
    (async () => {
      try {
        const res = await einkDisplayApi.getByDeviceType(selectedType.id);
        const tmpl = res.data?.template;
        if (tmpl && tmpl.pages?.length) {
          const loadedPages = tmpl.pages.map(p => ({
            name: p.name,
            elements: (p.elements || []).map(el => {
              if (!el.id) el.id = 'e' + (nextId++);
              else {
                const n = parseInt(el.id.slice(1));
                if (n >= nextId) nextId = n + 1;
              }
              return el;
            }),
            ...(p.buttons ? { buttons: p.buttons } : {}),
          }));
          setPages(loadedPages);
          setCurPage(tmpl.currentPage || 0);
        } else {
          setPages([{ name: 'Page 1', elements: [] }]);
          setCurPage(0);
        }
        setVersion(tmpl?.version || 0);
        setVariables(tmpl?.variables || {});
        setHomeTimeout(tmpl?.homeTimeout ?? 120);
        setNetworkPriority(tmpl?.networkPriority || ['ethernet', 'wifi', 'cellular']);
        setSelId(null);
        setDirty(false);
      } catch (err) {
        console.error('Failed to load e-ink template:', err);
      }
    })();
  }, [selectedType]);

  // ─── Refresh time variables periodically ────────────────────────
  // (No device-level variable polling needed; template vars are managed in the designer)

  // ─── Canvas tick for time variables ────────────────────────────
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const usedVars = detectVariables(pages);
    const hasTime = usedVars.some(v => ['time','time_s','second','minute','hour'].includes(v));
    if (!hasTime) return;
    const timer = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(timer);
  }, [pages]);

  // ─── Canvas rendering ──────────────────────────────────────────
  useEffect(() => {
    const cvs = canvasRef.current;
    if (!cvs) return;
    const ctx = cvs.getContext('2d');

    // White background
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, DW, DH);
    ctx.fillStyle = '#000';
    ctx.strokeStyle = '#000';

    const elements = curElements();
    for (const el of elements) {
      const x = el.x || 0, y = el.y || 0;

      if (el.type === 'text') {
        const sz = el.size || 16;
        const bold = el.bold ? 'bold ' : '';
        ctx.font = `${bold}${sz}px DejaVu Sans, sans-serif`;
        ctx.textBaseline = 'top';
        ctx.fillStyle = '#000';
        ctx.fillText(resolveVars(el.text || '', variables), x, y);

      } else if (el.type === 'icon') {
        const isz = el.size || 20;
        if (el.pixels?.length) {
          ctx.fillStyle = '#000';
          for (let row = 0; row < el.pixels.length; row++)
            for (let col = 0; col < el.pixels[row].length; col++)
              if (el.pixels[row][col] === '1') ctx.fillRect(x + col, y + row, 1, 1);
        } else {
          ctx.save();
          ctx.translate(x, y);
          ctx.fillStyle = '#000';
          ctx.strokeStyle = '#000';
          const fn = ICON_DRAW[el.name];
          if (fn) fn(ctx, isz);
          ctx.restore();
        }

      } else if (el.type === 'rect') {
        const rw = el.width || 50, rh = el.height || 30;
        ctx.lineWidth = Math.max(1, el.strokeWidth || 2);
        if (el.filled) {
          ctx.fillStyle = '#000'; ctx.fillRect(x, y, rw, rh);
        } else {
          ctx.strokeStyle = '#000'; ctx.strokeRect(x, y, rw, rh);
        }

      } else if (el.type === 'circle') {
        const cr = el.radius || 20;
        ctx.lineWidth = Math.max(1, el.strokeWidth || 2);
        ctx.beginPath(); ctx.arc(x, y, cr, 0, Math.PI * 2);
        if (el.filled) { ctx.fillStyle = '#000'; ctx.fill(); }
        else { ctx.strokeStyle = '#000'; ctx.stroke(); }

      } else if (el.type === 'line') {
        const lx2 = el.x2 ?? x + 50, ly2 = el.y2 ?? y;
        ctx.lineWidth = Math.max(1, el.strokeWidth || 2);
        ctx.strokeStyle = '#000';
        ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(lx2, ly2); ctx.stroke();

      } else if (el.type === 'image') {
        const iw = el.width || 60, ih = el.height || 60;
        if (el.pixels?.length) {
          ctx.fillStyle = '#000';
          for (let row = 0; row < el.pixels.length; row++)
            for (let col = 0; col < el.pixels[row].length; col++)
              if (el.pixels[row][col] === '1') ctx.fillRect(x + col, y + row, 1, 1);
        } else if (el.data) {
          let cached = imgCacheRef.current[el.data];
          if (!cached) {
            const img = new Image();
            img.onload = () => { imgCacheRef.current[el.data] = img; setTick(t => t + 1); };
            img.src = el.data;
            imgCacheRef.current[el.data] = 'loading';
          } else if (cached !== 'loading') {
            const bwData = renderBWPreview(cached, iw, ih, {
              inverted: !!el.inverted,
              ...(el.bwOpts || {}),
            });
            const ditherCanvas = document.createElement('canvas');
            ditherCanvas.width = iw; ditherCanvas.height = ih;
            ditherCanvas.getContext('2d').putImageData(bwData, 0, 0);
            ctx.drawImage(ditherCanvas, x, y);
          }
        }

      } else if (el.type === 'json') {
        const jw = el.width || 120, jh = el.height || 80;
        let fontSize = el.size || 12;
        const jbold = el.bold ? 'bold ' : '';
        const jsonStr = resolveVars(el.data || '{}', variables);
        let entries = [];
        try {
          const obj = JSON.parse(jsonStr);
          for (const jk of Object.keys(obj)) entries.push({ k: jk, v: String(obj[jk]) });
        } catch { entries = [{ k: 'error', v: 'Invalid JSON' }]; }

        // Auto-scale
        let lineH;
        while (fontSize > 6) {
          ctx.font = `${jbold}${fontSize}px DejaVu Sans, sans-serif`;
          lineH = Math.ceil(fontSize * 1.3);
          if ((entries.length * lineH) <= jh) break;
          fontSize--;
        }
        lineH = Math.ceil(fontSize * 1.3);

        ctx.textBaseline = 'top';
        ctx.fillStyle = '#000';
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 1;
        ctx.strokeRect(x, y, jw, jh);
        ctx.save();
        ctx.beginPath(); ctx.rect(x, y, jw, jh); ctx.clip();
        for (let ji = 0; ji < entries.length; ji++) {
          const jy = y + 2 + ji * lineH;
          if (jy + lineH > y + jh + lineH) break;
          const keyStr = entries[ji].k + ': ';
          ctx.font = `bold ${fontSize}px DejaVu Sans, sans-serif`;
          ctx.fillText(keyStr, x + 3, jy);
          const keyW = ctx.measureText(keyStr).width;
          ctx.font = `${fontSize}px DejaVu Sans, sans-serif`;
          ctx.fillText(entries[ji].v, x + 3 + keyW, jy);
        }
        ctx.restore();
      }
    }

    // Convert to 1-bit (matches e-paper)
    const imgData = ctx.getImageData(0, 0, DW, DH);
    for (let i = 0; i < imgData.data.length; i += 4) {
      const l = imgData.data[i]*0.299 + imgData.data[i+1]*0.587 + imgData.data[i+2]*0.114;
      const v = l < 128 ? 0 : 255;
      imgData.data[i] = imgData.data[i+1] = imgData.data[i+2] = v;
    }
    ctx.putImageData(imgData, 0, 0);
  }, [pages, curPage, selId, variables, tick]);

  // ─── Hit-test data for overlay ─────────────────────────────────
  const hitBoxes = useMemo(() => {
    const boxes = [];
    const elements = pages[curPage]?.elements || [];
    for (const el of elements) {
      const x = el.x || 0, y = el.y || 0;
      if (el.type === 'text') {
        const sz = el.size || 16;
        const tw = Math.max(20, (el.text || '').length * sz * 0.6);
        boxes.push({ id: el.id, x, y, w: tw, h: sz * 1.2 });
      } else if (el.type === 'icon') {
        const isz = el.size || 20;
        boxes.push({ id: el.id, x, y, w: isz, h: isz });
      } else if (el.type === 'rect') {
        boxes.push({ id: el.id, x, y, w: el.width || 50, h: el.height || 30, resizable: true });
      } else if (el.type === 'circle') {
        const cr = el.radius || 20;
        boxes.push({ id: el.id, x: x - cr, y: y - cr, w: cr*2, h: cr*2 });
      } else if (el.type === 'line') {
        const x2 = el.x2 ?? x+50, y2 = el.y2 ?? y;
        const minX = Math.min(x, x2) - 4, minY = Math.min(y, y2) - 4;
        const maxX = Math.max(x, x2) + 4, maxY = Math.max(y, y2) + 4;
        boxes.push({ id: el.id, x: minX, y: minY, w: maxX-minX, h: maxY-minY, isLine: true });
      } else if (el.type === 'image') {
        boxes.push({ id: el.id, x, y, w: el.width || 60, h: el.height || 60, resizable: true });
      } else if (el.type === 'json') {
        boxes.push({ id: el.id, x, y, w: el.width || 120, h: el.height || 80, resizable: true });
      }
    }
    return boxes;
  }, [pages, curPage, tick]);

  // ─── Mutation helper ───────────────────────────────────────────
  const updatePages = useCallback((fn) => {
    setPages(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      fn(next);
      return next;
    });
    setDirty(true);
  }, []);

  const updateElement = useCallback((id, updates) => {
    updatePages(pages => {
      const el = pages[curPage]?.elements?.find(e => e.id === id);
      if (el) Object.assign(el, updates);
    });
  }, [curPage, updatePages]);

  // ─── Add elements ──────────────────────────────────────────────
  const addElement = useCallback((type, extra = {}) => {
    const id = 'e' + (nextId++);
    const base = { id, type, x: Math.round(DW/2 - 25), y: Math.round(DH/2 - 10) };
    const defs = {
      text: { text: 'Hello', size: 18, bold: false },
      icon: { name: 'wifi', size: 24 },
      rect: { width: 60, height: 30, filled: false, strokeWidth: 2 },
      line: { x2: base.x + 80, y2: base.y, strokeWidth: 2 },
      circle: { radius: 20, filled: false, strokeWidth: 2, x: DW/2, y: DH/2 },
      image: { data: '', width: 60, height: 60 },
      json: { width: 120, height: 80, size: 12, bold: false, data: '{"key1":"value1","key2":"value2"}' },
    };
    updatePages(pages => {
      pages[curPage].elements.push({ ...base, ...defs[type], ...extra });
    });
    setSelId(id);
  }, [curPage, updatePages]);

  const addIcon = useCallback((name) => {
    const id = 'e' + (nextId++);
    updatePages(pages => {
      pages[curPage].elements.push({
        id, type: 'icon', name, size: 24,
        x: Math.round(DW/2 - 12), y: Math.round(DH/2 - 12),
      });
    });
    setSelId(id);
  }, [curPage, updatePages]);

  const deleteElement = useCallback(() => {
    if (!selId) return;
    updatePages(pages => {
      const els = pages[curPage].elements;
      const idx = els.findIndex(e => e.id === selId);
      if (idx >= 0) els.splice(idx, 1);
    });
    setSelId(null);
  }, [selId, curPage, updatePages]);

  // ─── Image upload ──────────────────────────────────────────────
  const handleImageUpload = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        let w = img.width, h = img.height;
        if (w > DW) { h = Math.round(h * DW / w); w = DW; }
        if (h > DH) { w = Math.round(w * DH / h); h = DH; }
        setImgPreview({ dataUrl: reader.result, width: w, height: h, mode: 'add' });
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  }, []);

  // Replace image on selected element
  const handleReplaceImage = useCallback((e) => {
    const file = e.target.files[0];
    if (!file || !selId) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const el = getEl(selId);
        if (!el) return;
        const maxW = el.width || DW, maxH = el.height || DH;
        let w = img.width, h = img.height;
        if (w > maxW) { h = Math.round(h * maxW / w); w = maxW; }
        if (h > maxH) { w = Math.round(w * maxH / h); h = maxH; }
        setImgPreview({ dataUrl: reader.result, width: w, height: h, mode: 'replace' });
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  }, [selId, getEl]);

  // ─── Pages management ──────────────────────────────────────────
  const addPage = useCallback(() => {
    updatePages(p => {
      p.push({ name: `Page ${p.length + 1}`, elements: [] });
    });
    setCurPage(pages.length);
    setSelId(null);
  }, [pages.length, updatePages]);

  const deletePage = useCallback((i) => {
    if (pages.length <= 1) return;
    updatePages(p => p.splice(i, 1));
    setCurPage(prev => prev >= pages.length - 1 ? pages.length - 2 : prev);
    setSelId(null);
  }, [pages.length, updatePages]);

  const renamePage = useCallback((i, name) => {
    updatePages(p => { p[i].name = name; });
  }, [updatePages]);

  // ─── Page reorder (drag-and-drop on page tabs) ─────────────────
  const [dragPageIdx, setDragPageIdx] = useState(null);
  const [dropTargetIdx, setDropTargetIdx] = useState(null);

  const movePage = useCallback((fromIdx, toIdx) => {
    if (fromIdx === toIdx) return;
    updatePages(p => {
      const [removed] = p.splice(fromIdx, 1);
      p.splice(toIdx, 0, removed);
      // Fix goto_page references in all button configs
      for (const pg of p) {
        if (!pg.buttons) continue;
        for (const btn of Object.values(pg.buttons)) {
          if (btn.action === 'goto_page' && btn.pageIndex != null) {
            if (btn.pageIndex === fromIdx) btn.pageIndex = toIdx;
            else if (fromIdx < btn.pageIndex && btn.pageIndex <= toIdx) btn.pageIndex--;
            else if (toIdx <= btn.pageIndex && btn.pageIndex < fromIdx) btn.pageIndex++;
          }
        }
      }
    });
    if (curPage === fromIdx) setCurPage(toIdx);
    else if (fromIdx < curPage && toIdx >= curPage) setCurPage(curPage - 1);
    else if (fromIdx > curPage && toIdx <= curPage) setCurPage(curPage + 1);
    setSelId(null);
  }, [curPage, updatePages]);

  // ─── Drag & Drop ───────────────────────────────────────────────
  const handleOverlayMouseDown = useCallback((e) => {
    // Find which element was clicked
    const rect = overlayRef.current?.getBoundingClientRect();
    if (!rect) return;
    const mx = (e.clientX - rect.left) / SCALE;
    const my = (e.clientY - rect.top) / SCALE;

    // Check resize handles first
    const resizeHandle = e.target.dataset?.resize;
    const targetId = e.target.dataset?.id || e.target.closest?.('[data-id]')?.dataset?.id;
    if (resizeHandle && targetId) {
      e.preventDefault();
      e.stopPropagation();
      const el = getEl(targetId);
      if (!el) return;
      setSelId(targetId);
      dragRef.current = {
        id: targetId, startX: e.clientX, startY: e.clientY,
        origX: el.x, origY: el.y,
        origW: el.width || 60, origH: el.height || 60,
        resize: resizeHandle,
      };
      return;
    }

    if (!targetId) {
      setSelId(null);
      return;
    }

    e.preventDefault();
    const el = getEl(targetId);
    if (!el) return;
    setSelId(targetId);
    dragRef.current = {
      id: targetId, startX: e.clientX, startY: e.clientY,
      origX: el.x, origY: el.y,
      origX2: el.x2, origY2: el.y2,
    };
  }, [getEl]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      const drag = dragRef.current;
      if (!drag) return;
      const dx = (e.clientX - drag.startX) / SCALE;
      const dy = (e.clientY - drag.startY) / SCALE;

      if (drag.resize) {
        updatePages(pages => {
          const el = pages[curPage]?.elements?.find(e => e.id === drag.id);
          if (!el) return;
          const dir = drag.resize;
          let nw = drag.origW, nh = drag.origH, nx = drag.origX, ny = drag.origY;
          const ratio = drag.origW / drag.origH;
          const keepR = el.keepRatio !== false;

          if (dir.includes('e')) nw = Math.max(4, Math.round((drag.origW + dx) / SNAP) * SNAP);
          if (dir.includes('w')) { nw = Math.max(4, Math.round((drag.origW - dx) / SNAP) * SNAP); nx = drag.origX + drag.origW - nw; }
          if (dir.includes('s')) nh = Math.max(4, Math.round((drag.origH + dy) / SNAP) * SNAP);
          if (dir.includes('n')) { nh = Math.max(4, Math.round((drag.origH - dy) / SNAP) * SNAP); ny = drag.origY + drag.origH - nh; }

          if (keepR && dir.length === 2) {
            const rw = nw / drag.origW, rh = nh / drag.origH;
            if (Math.abs(rw - 1) > Math.abs(rh - 1)) {
              nh = Math.max(4, Math.round(nw / ratio / SNAP) * SNAP);
              if (dir.includes('n')) ny = drag.origY + drag.origH - nh;
            } else {
              nw = Math.max(4, Math.round(nh * ratio / SNAP) * SNAP);
              if (dir.includes('w')) nx = drag.origX + drag.origW - nw;
            }
          }

          el.width = nw; el.height = nh;
          el.x = Math.max(0, nx); el.y = Math.max(0, ny);
          delete el.pixels;
        });
        return;
      }

      updatePages(pages => {
        const el = pages[curPage]?.elements?.find(e => e.id === drag.id);
        if (!el) return;
        el.x = Math.max(0, Math.min(DW, Math.round((drag.origX + dx) / SNAP) * SNAP));
        el.y = Math.max(0, Math.min(DH, Math.round((drag.origY + dy) / SNAP) * SNAP));
        if (el.type === 'line' && drag.origX2 !== undefined) {
          el.x2 = Math.max(0, Math.min(DW, Math.round((drag.origX2 + dx) / SNAP) * SNAP));
          el.y2 = Math.max(0, Math.min(DH, Math.round((drag.origY2 + dy) / SNAP) * SNAP));
        }
      });
    };

    const handleMouseUp = () => { dragRef.current = null; };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [curPage, updatePages]);

  // ─── Keyboard shortcuts ────────────────────────────────────────
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (['INPUT','TEXTAREA','SELECT'].includes(e.target.tagName)) return;
      if ((e.key === 'Delete' || e.key === 'Backspace') && selId) {
        deleteElement();
        return;
      }
      if (e.key === 'Escape') { setSelId(null); return; }
      if (!selId) return;
      const step = e.shiftKey ? 10 : 2;
      const move = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
      if (move[e.key]) {
        e.preventDefault();
        const [dx, dy] = move[e.key];
        updateElement(selId, {});
        updatePages(pages => {
          const el = pages[curPage]?.elements?.find(e => e.id === selId);
          if (el) {
            el.x = Math.max(0, Math.min(DW, el.x + dx));
            el.y = Math.max(0, Math.min(DH, el.y + dy));
          }
        });
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selId, curPage, deleteElement, updateElement, updatePages]);

  // ─── Save & Push ───────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    if (!selectedType) return;
    setSaving(true);
    try {
      // Pre-render pixels for icons and images
      const pagesClone = JSON.parse(JSON.stringify(pages));
      for (const p of pagesClone) {
        for (const el of p.elements) {
          if (el.type === 'icon' && !el.pixels) {
            el.pixels = iconToPixels(el.name, el.size || 20);
          }
          if (el.type === 'image' && el.data && !el.pixels) {
            el.pixels = await imageToPixels(el.data, el.width || 60, el.height || 60, !!el.inverted, el.bwOpts || {});
          }
        }
      }
      const res = await einkDisplayApi.saveTemplate(selectedType.id, { pages: pagesClone, currentPage: curPage, homeTimeout, networkPriority });
      setVersion(res.data.version);
      setDirty(false);
      enqueueSnackbar(t('EinkDesigner.saved'), { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(t('EinkDesigner.save_failed'), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  }, [selectedType, pages, curPage, homeTimeout, networkPriority, enqueueSnackbar, t]);

  const handlePush = useCallback(async () => {
    if (!selectedType) return;
    setPushing(true);
    try {
      await handleSave();
      await einkDisplayApi.push(selectedType.id);
      enqueueSnackbar(t('EinkDesigner.pushed'), { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(t('EinkDesigner.push_failed'), { variant: 'error' });
    } finally {
      setPushing(false);
    }
  }, [selectedType, handleSave, enqueueSnackbar, t]);

  // ─── Property setters ──────────────────────────────────────────
  const setProp = useCallback((key, val) => {
    if (!selId) return;
    updatePages(pages => {
      const el = pages[curPage]?.elements?.find(e => e.id === selId);
      if (!el) return;
      el[key] = val;
      if (el.type === 'icon' && (key === 'name' || key === 'size') && el.pixels) {
        el.pixels = iconToPixels(el.name, el.size || 20);
      }
      if (el.type === 'image' && (key === 'width' || key === 'height' || key === 'inverted')) {
        delete el.pixels;
      }
    });
  }, [selId, curPage, updatePages]);

  // ─── Selected element ──────────────────────────────────────────
  const selectedEl = selId ? getEl(selId) : null;

  // ─── Variable panel helpers ────────────────────────────────────
  const usedVars = useMemo(() => detectVariables(pages), [pages]);
  const customVars = usedVars.filter(v => !BUILTIN_VARS.includes(v));
  const usedBuiltins = usedVars.filter(v => BUILTIN_VARS.includes(v));

  // ─── Render ────────────────────────────────────────────────────
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
          {t('EinkDesigner.title')}
        </Typography>

        <Autocomplete
          size="small"
          sx={{ width: 300 }}
          options={deviceTypes}
          loading={loadingTypes}
          getOptionLabel={(dt) => dt.FrontEndName || dt.friendlyName || `Type ${dt.id}`}
          value={selectedType}
          onChange={(_, v) => setSelectedType(v)}
          renderInput={(params) => (
            <TextField {...params} label={t('EinkDesigner.select_device_type')} size="small" />
          )}
        />

        {selectedType && (
          <Chip label={`v${version}`} size="small" variant="outlined" sx={{ fontFamily: 'monospace' }} />
        )}

        {selectedType && (
          <Tooltip title={t('EinkDesigner.home_timeout_hint')}>
            <TextField
              size="small"
              type="number"
              label={t('EinkDesigner.home_timeout')}
              value={homeTimeout}
              onChange={(e) => { setHomeTimeout(Math.max(0, parseInt(e.target.value) || 0)); setDirty(true); }}
              inputProps={{ min: 0, step: 10, style: { width: 50 } }}
              sx={{ width: 130 }}
            />
          </Tooltip>
        )}

        {selectedType && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="caption" sx={{ mr: 0.5 }}>{t('EinkDesigner.net_priority') || 'Priority'}:</Typography>
            {networkPriority.map((type, idx) => (
              <Box key={type} sx={{ display: 'flex', alignItems: 'center' }}>
                <Chip
                  label={{ ethernet: 'Eth', wifi: 'WiFi', cellular: 'Cell' }[type] || type}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: 11, height: 22 }}
                />
                {idx < networkPriority.length - 1 && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', ml: -0.3, mr: -0.3 }}>
                    <IconButton
                      size="small"
                      sx={{ p: 0, height: 12, width: 16 }}
                      onClick={() => {
                        const np = [...networkPriority];
                        [np[idx], np[idx + 1]] = [np[idx + 1], np[idx]];
                        setNetworkPriority(np);
                        setDirty(true);
                      }}
                    >
                      <ArrowDownward sx={{ fontSize: 10 }} />
                    </IconButton>
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}

        <Box sx={{ flex: 1 }} />

        {dirty && <Chip label={t('EinkDesigner.unsaved')} color="warning" size="small" />}

        <Button
          variant="outlined"
          size="small"
          startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
          onClick={handleSave}
          disabled={!selectedType || saving}
        >
          {t('EinkDesigner.save')}
        </Button>
        <Button
          variant="contained"
          size="small"
          color="success"
          startIcon={pushing ? <CircularProgress size={16} color="inherit" /> : <PushIcon />}
          onClick={handlePush}
          disabled={!selectedType || pushing}
        >
          {t('EinkDesigner.push_to_display')}
        </Button>
      </Box>

      {!selectedType ? (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Alert severity="info" sx={{ maxWidth: 400 }}>
            {t('EinkDesigner.select_info')}
          </Alert>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* ─── Left Sidebar: Tools ─── */}
          <Paper
            elevation={0}
            sx={{
              width: 200, flexShrink: 0, p: 1.5, overflow: 'auto',
              borderRight: 1, borderColor: 'divider',
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 0.5 }}>
              {t('EinkDesigner.elements')}
            </Typography>
            {[
              { type: 'text', icon: <TextIcon fontSize="small" />, label: t('EinkDesigner.text') },
              { type: 'rect', icon: <RectIcon fontSize="small" />, label: t('EinkDesigner.rectangle') },
              { type: 'line', icon: <LineIcon fontSize="small" />, label: t('EinkDesigner.line') },
              { type: 'circle', icon: <CircleIcon fontSize="small" />, label: t('EinkDesigner.circle') },
              { type: 'json', icon: <JsonIcon fontSize="small" />, label: t('EinkDesigner.json_data') },
            ].map(({ type, icon, label }) => (
              <Button
                key={type}
                fullWidth
                size="small"
                startIcon={icon}
                onClick={() => addElement(type)}
                sx={{ justifyContent: 'flex-start', mb: 0.5, textTransform: 'none' }}
              >
                {label}
              </Button>
            ))}

            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1, display: 'block', mt: 1.5, mb: 0.5 }}>
              {t('EinkDesigner.icons')}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0.5 }}>
              {ICON_NAMES.map(name => (
                <Tooltip key={name} title={name} placement="top">
                  <IconButton
                    size="small"
                    onClick={() => addIcon(name)}
                    sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 0.5 }}
                  >
                    <img
                      src={renderIconToDataURL(name, 18)}
                      alt={name}
                      width={18} height={18}
                      style={{ filter: 'invert(0.5)' }}
                    />
                  </IconButton>
                </Tooltip>
              ))}
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1, display: 'block', mt: 1.5, mb: 0.5 }}>
              {t('EinkDesigner.image')}
            </Typography>
            <Button
              fullWidth size="small"
              startIcon={<ImageIcon fontSize="small" />}
              onClick={() => fileInputRef.current?.click()}
              sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
            >
              {t('EinkDesigner.upload_image')}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleImageUpload}
            />

            <Divider sx={{ my: 1.5 }} />

            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 0.5 }}>
              {t('EinkDesigner.variables')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, display: 'block', mb: 0.5 }}>
              {t('EinkDesigner.variables_hint')}
            </Typography>
            {customVars.length > 0 && (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, fontWeight: 700 }}>{t('EinkDesigner.custom')}</Typography>
                {customVars.map(name => (
                  <TextField
                    key={name}
                    label={`{{${name}}}`}
                    size="small"
                    fullWidth
                    value={variables[name] || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      setVariables(prev => ({ ...prev, [name]: val }));
                    }}
                    sx={{ mb: 0.5, '& .MuiInputBase-input': { fontSize: 12, py: 0.5 } }}
                  />
                ))}
              </>
            )}
            {usedBuiltins.length > 0 && (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, mt: 0.5, display: 'block', fontWeight: 700 }}>{t('EinkDesigner.in_use')}</Typography>
                {usedBuiltins.map(name => (
                  <Typography key={name} variant="caption" sx={{ display: 'block', fontSize: 11, color: 'text.secondary' }}>
                    <span style={{ color: '#3b82f6' }}>{`{{${name}}}`}</span> = {variables[name] || getLocalTimeVars()[name] || '...'}
                  </Typography>
                ))}
              </>
            )}
            {Object.entries(VAR_GROUPS).map(([group, vars]) => {
              const unusedVars = vars.filter(v => !usedVars.includes(v));
              if (unusedVars.length === 0) return null;
              const labels = { time: 'Time', system: 'System', sensor: 'Sensor', device: 'Device', agent: 'Agent', sip: 'SIP', services: 'Services', network: 'Network' };
              return (
                <Box key={group} sx={{ mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, fontWeight: 700 }}>
                    {t(`EinkDesigner.var_group_${group}`) || labels[group]}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.3 }}>
                    {unusedVars.map(name => (
                      <Chip
                        key={name}
                        label={name}
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          navigator.clipboard.writeText(`{{${name}}}`).catch(() => {});
                          enqueueSnackbar(`{{${name}}} copied`, { variant: 'info', autoHideDuration: 1500 });
                        }}
                        sx={{ fontSize: 10, height: 20, cursor: 'pointer', '& .MuiChip-label': { px: 0.5 } }}
                      />
                    ))}
                  </Box>
                </Box>
              );
            })}
          </Paper>

          {/* ─── Canvas Area ─── */}
          <Box
            sx={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: 'action.hover', overflow: 'auto',
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) setSelId(null);
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <Box
                sx={{
                  position: 'relative',
                  width: CW, height: CH,
                  border: '3px solid',
                  borderColor: 'text.primary',
                  cursor: 'crosshair',
                  overflow: 'hidden',
                }}
              >
                <canvas
                  ref={canvasRef}
                  width={DW}
                  height={DH}
                  style={{
                    position: 'absolute', inset: 0,
                    width: CW, height: CH,
                    imageRendering: 'pixelated',
                  }}
                />
                {/* Interaction overlay */}
                <Box
                  ref={overlayRef}
                  onMouseDown={handleOverlayMouseDown}
                  sx={{
                    position: 'absolute', inset: 0,
                    width: CW, height: CH,
                  }}
                >
                  {hitBoxes.map(hb => (
                    <Box
                      key={hb.id}
                      data-id={hb.id}
                      sx={{
                        position: 'absolute',
                        left: hb.x * SCALE,
                        top: hb.y * SCALE,
                        width: hb.w * SCALE,
                        height: hb.h * SCALE,
                        cursor: 'move',
                        outline: hb.id === selId ? '2px solid #3b82f6' : 'none',
                        outlineOffset: 1,
                        '&:hover': { outline: hb.id !== selId ? '1px dashed rgba(59,130,246,0.4)' : undefined },
                      }}
                    >
                      {hb.id === selId && hb.resizable && (
                        ['nw','n','ne','w','e','sw','s','se'].map(dir => (
                          <Box
                            key={dir}
                            data-id={hb.id}
                            data-resize={dir}
                            sx={{
                              position: 'absolute',
                              width: 10, height: 10,
                              bgcolor: 'primary.main',
                              border: '1px solid white',
                              zIndex: 10,
                              ...(dir === 'nw' && { top: -5, left: -5, cursor: 'nwse-resize' }),
                              ...(dir === 'n' && { top: -5, left: '50%', ml: '-5px', cursor: 'ns-resize' }),
                              ...(dir === 'ne' && { top: -5, right: -5, cursor: 'nesw-resize' }),
                              ...(dir === 'w' && { top: '50%', left: -5, mt: '-5px', cursor: 'ew-resize' }),
                              ...(dir === 'e' && { top: '50%', right: -5, mt: '-5px', cursor: 'ew-resize' }),
                              ...(dir === 'sw' && { bottom: -5, left: -5, cursor: 'nesw-resize' }),
                              ...(dir === 's' && { bottom: -5, left: '50%', ml: '-5px', cursor: 'ns-resize' }),
                              ...(dir === 'se' && { bottom: -5, right: -5, cursor: 'nwse-resize' }),
                            }}
                          />
                        ))
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', mt: 0.5 }}>
                {t('EinkDesigner.canvas_label', { width: DW, height: DH, scale: SCALE })}
              </Typography>
            </Box>
          </Box>

          {/* ─── Right Sidebar: Properties ─── */}
          <Paper
            elevation={0}
            sx={{
              width: 230, flexShrink: 0, p: 1.5, overflow: 'auto',
              borderLeft: 1, borderColor: 'divider',
            }}
          >
            {!selectedEl ? (
              <Box sx={{ textAlign: 'center', mt: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('EinkDesigner.select_element')}
                </Typography>
              </Box>
            ) : (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                  {t(`EinkDesigner.${selectedEl.type}`) || selectedEl.type}
                </Typography>

                {/* Position */}
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <TextField
                    label="x" size="small" type="number" fullWidth
                    value={selectedEl.x || 0}
                    onChange={(e) => setProp('x', Number(e.target.value))}
                    sx={{ '& .MuiInputBase-input': { fontSize: 12, py: 0.5 } }}
                  />
                  <TextField
                    label="y" size="small" type="number" fullWidth
                    value={selectedEl.y || 0}
                    onChange={(e) => setProp('y', Number(e.target.value))}
                    sx={{ '& .MuiInputBase-input': { fontSize: 12, py: 0.5 } }}
                  />
                </Stack>

                {/* Type-specific properties */}
                {selectedEl.type === 'text' && (
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    <TextField
                      label="text" size="small" fullWidth
                      value={selectedEl.text || ''}
                      onChange={(e) => setProp('text', e.target.value)}
                      sx={{ '& .MuiInputBase-input': { fontSize: 12 } }}
                    />
                    <TextField
                      label="size" size="small" type="number" fullWidth
                      value={selectedEl.size || 16}
                      onChange={(e) => setProp('size', Number(e.target.value))}
                      sx={{ '& .MuiInputBase-input': { fontSize: 12, py: 0.5 } }}
                    />
                    <FormControlLabel
                      control={<Checkbox size="small" checked={!!selectedEl.bold} onChange={(e) => setProp('bold', e.target.checked)} />}
                      label={t('EinkDesigner.bold')}
                      sx={{ '& .MuiTypography-root': { fontSize: 12 } }}
                    />
                  </Stack>
                )}

                {selectedEl.type === 'icon' && (
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    <FormControl size="small" fullWidth>
                      <InputLabel sx={{ fontSize: 12 }}>Icon</InputLabel>
                      <Select
                        label="Icon"
                        value={selectedEl.name || 'wifi'}
                        onChange={(e) => setProp('name', e.target.value)}
                        sx={{ fontSize: 12 }}
                      >
                        {ICON_NAMES.map(n => (
                          <MenuItem key={n} value={n} sx={{ fontSize: 12 }}>{n}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      label="size" size="small" type="number" fullWidth
                      value={selectedEl.size || 20}
                      onChange={(e) => setProp('size', Number(e.target.value))}
                      sx={{ '& .MuiInputBase-input': { fontSize: 12, py: 0.5 } }}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        const el = getEl(selId);
                        if (!el) return;
                        const initPixels = el.pixels || iconToPixels(el.name, el.size || 20);
                        setPixelEditorEl(el);
                        setPixelEditorInitPixels(initPixels);
                        setPixelEditorOpen(true);
                      }}
                      sx={{ textTransform: 'none', fontSize: 12 }}
                    >
                      {selectedEl.pixels ? t('EinkDesigner.edit_pixels_custom') : t('EinkDesigner.edit_pixels')}
                    </Button>
                    {selectedEl.pixels && (
                      <Button
                        size="small" color="error" variant="text"
                        onClick={() => setProp('pixels', undefined)}
                        sx={{ textTransform: 'none', fontSize: 12 }}
                      >
                        {t('EinkDesigner.reset_to_icon')}
                      </Button>
                    )}
                  </Stack>
                )}

                {selectedEl.type === 'rect' && (
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    <Stack direction="row" spacing={1}>
                      <TextField
                        label="width" size="small" type="number" fullWidth
                        value={selectedEl.width || 50}
                        onChange={(e) => setProp('width', Number(e.target.value))}
                        sx={{ '& .MuiInputBase-input': { fontSize: 12, py: 0.5 } }}
                      />
                      <TextField
                        label="height" size="small" type="number" fullWidth
                        value={selectedEl.height || 30}
                        onChange={(e) => setProp('height', Number(e.target.value))}
                        sx={{ '& .MuiInputBase-input': { fontSize: 12, py: 0.5 } }}
                      />
                    </Stack>
                    <TextField
                      label="strokeWidth" size="small" type="number" fullWidth
                      value={selectedEl.strokeWidth || 2}
                      onChange={(e) => setProp('strokeWidth', Number(e.target.value))}
                      sx={{ '& .MuiInputBase-input': { fontSize: 12, py: 0.5 } }}
                    />
                    <FormControlLabel
                      control={<Checkbox size="small" checked={!!selectedEl.filled} onChange={(e) => setProp('filled', e.target.checked)} />}
                      label={t('EinkDesigner.filled')}
                      sx={{ '& .MuiTypography-root': { fontSize: 12 } }}
                    />
                  </Stack>
                )}

                {selectedEl.type === 'line' && (
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    <Stack direction="row" spacing={1}>
                      <TextField
                        label="x2" size="small" type="number" fullWidth
                        value={selectedEl.x2 ?? (selectedEl.x || 0) + 50}
                        onChange={(e) => setProp('x2', Number(e.target.value))}
                        sx={{ '& .MuiInputBase-input': { fontSize: 12, py: 0.5 } }}
                      />
                      <TextField
                        label="y2" size="small" type="number" fullWidth
                        value={selectedEl.y2 ?? (selectedEl.y || 0)}
                        onChange={(e) => setProp('y2', Number(e.target.value))}
                        sx={{ '& .MuiInputBase-input': { fontSize: 12, py: 0.5 } }}
                      />
                    </Stack>
                    <TextField
                      label="strokeWidth" size="small" type="number" fullWidth
                      value={selectedEl.strokeWidth || 2}
                      onChange={(e) => setProp('strokeWidth', Number(e.target.value))}
                      sx={{ '& .MuiInputBase-input': { fontSize: 12, py: 0.5 } }}
                    />
                  </Stack>
                )}

                {selectedEl.type === 'circle' && (
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    <TextField
                      label="radius" size="small" type="number" fullWidth
                      value={selectedEl.radius || 20}
                      onChange={(e) => setProp('radius', Number(e.target.value))}
                      sx={{ '& .MuiInputBase-input': { fontSize: 12, py: 0.5 } }}
                    />
                    <TextField
                      label="strokeWidth" size="small" type="number" fullWidth
                      value={selectedEl.strokeWidth || 2}
                      onChange={(e) => setProp('strokeWidth', Number(e.target.value))}
                      sx={{ '& .MuiInputBase-input': { fontSize: 12, py: 0.5 } }}
                    />
                    <FormControlLabel
                      control={<Checkbox size="small" checked={!!selectedEl.filled} onChange={(e) => setProp('filled', e.target.checked)} />}
                      label={t('EinkDesigner.filled')}
                      sx={{ '& .MuiTypography-root': { fontSize: 12 } }}
                    />
                  </Stack>
                )}

                {selectedEl.type === 'image' && (
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    <Stack direction="row" spacing={1}>
                      <TextField
                        label="width" size="small" type="number" fullWidth
                        value={selectedEl.width || 60}
                        onChange={(e) => setProp('width', Number(e.target.value))}
                        sx={{ '& .MuiInputBase-input': { fontSize: 12, py: 0.5 } }}
                      />
                      <TextField
                        label="height" size="small" type="number" fullWidth
                        value={selectedEl.height || 60}
                        onChange={(e) => setProp('height', Number(e.target.value))}
                        sx={{ '& .MuiInputBase-input': { fontSize: 12, py: 0.5 } }}
                      />
                    </Stack>
                    <FormControlLabel
                      control={<Checkbox size="small" checked={selectedEl.keepRatio !== false} onChange={(e) => setProp('keepRatio', e.target.checked)} />}
                      label={t('EinkDesigner.keep_ratio')}
                      sx={{ '& .MuiTypography-root': { fontSize: 12 } }}
                    />
                    <FormControlLabel
                      control={<Checkbox size="small" checked={!!selectedEl.inverted} onChange={(e) => setProp('inverted', e.target.checked)} />}
                      label={t('EinkDesigner.inverted')}
                      sx={{ '& .MuiTypography-root': { fontSize: 12 } }}
                    />
                    <Button
                      size="small" variant="outlined"
                      onClick={() => replaceInputRef.current?.click()}
                      sx={{ textTransform: 'none', fontSize: 12 }}
                    >
                      {t('EinkDesigner.replace_image')}
                    </Button>
                    <input
                      ref={replaceInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={handleReplaceImage}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={async () => {
                        const el = getEl(selId);
                        if (!el) return;
                        let initPx = el.pixels;
                        if (!initPx && el.data) {
                          initPx = await imageToPixels(el.data, el.width || 60, el.height || 60, !!el.inverted);
                        }
                        if (!initPx) {
                          const w = el.width || 60, h = el.height || 60;
                          initPx = Array.from({ length: h }, () => '0'.repeat(w));
                        }
                        setPixelEditorEl(el);
                        setPixelEditorInitPixels(initPx);
                        setPixelEditorOpen(true);
                      }}
                      sx={{ textTransform: 'none', fontSize: 12 }}
                    >
                      {selectedEl.pixels ? t('EinkDesigner.edit_pixels_custom') : t('EinkDesigner.edit_pixels')}
                    </Button>
                    {selectedEl.pixels && (
                      <Button
                        size="small" color="error" variant="text"
                        onClick={() => setProp('pixels', undefined)}
                        sx={{ textTransform: 'none', fontSize: 12 }}
                      >
                        {t('EinkDesigner.reset_from_image')}
                      </Button>
                    )}
                  </Stack>
                )}

                {selectedEl.type === 'json' && (
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    <Stack direction="row" spacing={1}>
                      <TextField
                        label="width" size="small" type="number" fullWidth
                        value={selectedEl.width || 120}
                        onChange={(e) => setProp('width', Number(e.target.value))}
                        sx={{ '& .MuiInputBase-input': { fontSize: 12, py: 0.5 } }}
                      />
                      <TextField
                        label="height" size="small" type="number" fullWidth
                        value={selectedEl.height || 80}
                        onChange={(e) => setProp('height', Number(e.target.value))}
                        sx={{ '& .MuiInputBase-input': { fontSize: 12, py: 0.5 } }}
                      />
                    </Stack>
                    <TextField
                      label="size" size="small" type="number" fullWidth
                      value={selectedEl.size || 12}
                      onChange={(e) => setProp('size', Number(e.target.value))}
                      sx={{ '& .MuiInputBase-input': { fontSize: 12, py: 0.5 } }}
                    />
                    <FormControlLabel
                      control={<Checkbox size="small" checked={!!selectedEl.bold} onChange={(e) => setProp('bold', e.target.checked)} />}
                      label={t('EinkDesigner.bold')}
                      sx={{ '& .MuiTypography-root': { fontSize: 12 } }}
                    />
                    <TextField
                      label="JSON data" size="small" fullWidth multiline rows={4}
                      value={selectedEl.data || '{}'}
                      onChange={(e) => setProp('data', e.target.value)}
                      sx={{ '& .MuiInputBase-input': { fontSize: 11, fontFamily: 'monospace' } }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                      {t('EinkDesigner.json_tip')}
                    </Typography>
                  </Stack>
                )}

                <Button
                  fullWidth
                  variant="contained"
                  color="error"
                  size="small"
                  startIcon={<DeleteIcon />}
                  onClick={deleteElement}
                  sx={{ mt: 2, textTransform: 'none' }}
                >
                  {t('EinkDesigner.delete_element')}
                </Button>
              </>
            )}
          </Paper>
        </Box>
      )}

      {/* ─── Bottom Bar: Pages ─── */}
      {selectedType && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 2, py: 0.5, borderTop: 1, borderColor: 'divider' }}>
          {pages.map((p, i) => (
            <React.Fragment key={i}>
              <Chip
                label={p.name}
                size="small"
                color={i === curPage ? 'primary' : 'default'}
                variant={i === curPage ? 'filled' : 'outlined'}
                onClick={() => { setCurPage(i); setSelId(null); }}
                onDoubleClick={() => {
                  const name = prompt(t('EinkDesigner.page_name_prompt'), p.name);
                  if (name) renamePage(i, name);
                }}
                draggable
                onDragStart={(e) => { setDragPageIdx(i); e.dataTransfer.effectAllowed = 'move'; }}
                onDragOver={(e) => { e.preventDefault(); setDropTargetIdx(i); }}
                onDrop={(e) => {
                  e.preventDefault();
                  if (dragPageIdx != null && dragPageIdx !== i) movePage(dragPageIdx, i);
                  setDragPageIdx(null); setDropTargetIdx(null);
                }}
                onDragEnd={() => { setDragPageIdx(null); setDropTargetIdx(null); }}
                sx={{
                  cursor: 'grab',
                  opacity: dragPageIdx === i ? 0.4 : 1,
                  borderLeft: dropTargetIdx === i && dragPageIdx != null && dragPageIdx !== i ? '3px solid' : undefined,
                  borderLeftColor: dropTargetIdx === i && dragPageIdx != null && dragPageIdx !== i ? 'primary.main' : undefined,
                }}
              />
              <Tooltip title={t('EinkDesigner.configure_buttons')}>
                <IconButton size="small" onClick={() => setButtonConfigPage(i)} sx={{ p: 0, ml: -0.5 }}>
                  <TuneIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
              {pages.length > 1 && (
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => deletePage(i)}
                  sx={{ p: 0, ml: -0.5 }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              )}
            </React.Fragment>
          ))}
          <Button
            size="small"
            variant="text"
            startIcon={<AddIcon />}
            onClick={addPage}
            sx={{ textTransform: 'none', fontSize: 12 }}
          >
            {t('EinkDesigner.page')}
          </Button>
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            v{version}
          </Typography>
        </Box>
      )}

      {/* ─── Button Config Dialog ─── */}
      <ButtonConfigDialog
        open={buttonConfigPage !== null}
        page={buttonConfigPage !== null ? pages[buttonConfigPage] : null}
        pageIndex={buttonConfigPage}
        allPages={pages}
        t={t}
        onClose={() => setButtonConfigPage(null)}
        onApply={(buttons) => {
          if (buttonConfigPage !== null) {
            updatePages(p => { p[buttonConfigPage].buttons = buttons; });
          }
          setButtonConfigPage(null);
        }}
      />

      {/* ─── Pixel Editor Dialog ─── */}
      <PixelEditorDialog
        open={pixelEditorOpen}
        element={pixelEditorEl || getEl(selId)}
        initPixels={pixelEditorInitPixels}
        onClose={() => { setPixelEditorOpen(false); setPixelEditorInitPixels(null); }}
        onUpdate={(pixels) => {
          if (selId) {
            updateElement(selId, { pixels });
          }
          setPixelEditorOpen(false);
          setPixelEditorInitPixels(null);
        }}
      />

      {/* ─── Image B&W Preview Dialog ─── */}
      <ImagePreviewDialog
        open={!!imgPreview}
        dataUrl={imgPreview?.dataUrl}
        width={imgPreview?.width || 60}
        height={imgPreview?.height || 60}
        t={t}
        onClose={() => setImgPreview(null)}
        onAccept={(opts) => {
          if (!imgPreview) return;
          const { width, height, dataUrl, mode } = imgPreview;
          if (mode === 'replace' && selId) {
            updateElement(selId, { data: dataUrl, width, height, pixels: undefined, bwOpts: opts });
          } else {
            addElement('image', { data: dataUrl, width, height, keepRatio: true, bwOpts: opts });
          }
          setImgPreview(null);
        }}
      />
    </Box>
  );
}

// ─── Button Config Dialog ─────────────────────────────────────────
const BUTTON_ACTIONS = [
  'prev_page', 'next_page', 'goto_page', 'refresh', 'refresh_vars',
  'toggle_var', 'cycle_var', 'api_call', 'do_nothing',
];

const DEFAULT_BUTTONS = {
  '1': { action: 'prev_page' },
  '2': { action: 'next_page' },
  '3': { action: 'refresh' },
  '4': { action: 'do_nothing' },
};

function ButtonConfigDialog({ open, page, pageIndex, allPages, t, onClose, onApply }) {
  const [buttons, setButtons] = useState({});

  useEffect(() => {
    if (!open || !page) return;
    setButtons({
      '1': { ...DEFAULT_BUTTONS['1'], ...(page.buttons?.['1'] || {}) },
      '2': { ...DEFAULT_BUTTONS['2'], ...(page.buttons?.['2'] || {}) },
      '3': { ...DEFAULT_BUTTONS['3'], ...(page.buttons?.['3'] || {}) },
      '4': { ...DEFAULT_BUTTONS['4'], ...(page.buttons?.['4'] || {}) },
    });
  }, [open, page]);

  const updateBtn = (key, updates) => {
    setButtons(prev => ({ ...prev, [key]: { ...prev[key], ...updates } }));
  };

  const setAction = (key, action) => {
    // Reset to just the action but keep label
    setButtons(prev => ({ ...prev, [key]: { action, label: prev[key]?.label || '' } }));
  };

  const renderParams = (key, cfg) => {
    switch (cfg.action) {
      case 'goto_page':
        return (
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t('EinkDesigner.target_page')}</InputLabel>
            <Select
              value={cfg.pageIndex ?? ''}
              label={t('EinkDesigner.target_page')}
              onChange={e => updateBtn(key, { pageIndex: e.target.value })}
            >
              {(allPages || []).map((p, i) => (
                <MenuItem key={i} value={i} disabled={i === pageIndex}>
                  {p.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case 'toggle_var':
        return (
          <>
            <TextField
              size="small" label={t('EinkDesigner.variable_name')}
              value={cfg.variable || ''}
              onChange={e => updateBtn(key, { variable: e.target.value })}
              sx={{ width: 120 }}
            />
            <TextField
              size="small" label="A"
              value={cfg.values?.[0] || ''}
              onChange={e => updateBtn(key, { values: [e.target.value, cfg.values?.[1] || ''] })}
              sx={{ width: 80 }}
            />
            <TextField
              size="small" label="B"
              value={cfg.values?.[1] || ''}
              onChange={e => updateBtn(key, { values: [cfg.values?.[0] || '', e.target.value] })}
              sx={{ width: 80 }}
            />
          </>
        );
      case 'cycle_var':
        return (
          <>
            <TextField
              size="small" label={t('EinkDesigner.variable_name')}
              value={cfg.variable || ''}
              onChange={e => updateBtn(key, { variable: e.target.value })}
              sx={{ width: 120 }}
            />
            <TextField
              size="small" label={t('EinkDesigner.cycle_values')}
              value={(cfg.values || []).join(', ')}
              onChange={e => updateBtn(key, { values: e.target.value.split(',').map(v => v.trim()).filter(Boolean) })}
              sx={{ width: 180 }}
            />
          </>
        );
      case 'api_call':
        return (
          <>
            <TextField
              size="small" label={t('EinkDesigner.endpoint')}
              value={cfg.endpoint || ''}
              onChange={e => updateBtn(key, { endpoint: e.target.value })}
              sx={{ width: 180 }}
              placeholder="/custom-action"
            />
            <FormControl size="small" sx={{ minWidth: 90 }}>
              <InputLabel>{t('EinkDesigner.method')}</InputLabel>
              <Select
                value={cfg.method || 'POST'}
                label={t('EinkDesigner.method')}
                onChange={e => updateBtn(key, { method: e.target.value })}
              >
                <MenuItem value="GET">GET</MenuItem>
                <MenuItem value="POST">POST</MenuItem>
              </Select>
            </FormControl>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {t('EinkDesigner.button_config_title', { pageName: page?.name || '' })}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {['1', '2', '3', '4'].map(key => {
            const cfg = buttons[key] || DEFAULT_BUTTONS[key];
            return (
              <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', minWidth: 50, fontFamily: 'monospace' }}>
                  {t('EinkDesigner.button_key', { num: key })}
                </Typography>
                <FormControl size="small" sx={{ minWidth: 170 }}>
                  <InputLabel>{t('EinkDesigner.action')}</InputLabel>
                  <Select
                    value={cfg.action}
                    label={t('EinkDesigner.action')}
                    onChange={e => setAction(key, e.target.value)}
                  >
                    {BUTTON_ACTIONS.map(a => (
                      <MenuItem key={a} value={a}>{t(`EinkDesigner.action_${a}`)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  label={t('EinkDesigner.button_label')}
                  value={cfg.label || ''}
                  onChange={e => updateBtn(key, { label: e.target.value })}
                  sx={{ width: 120 }}
                  placeholder={t('EinkDesigner.button_label_hint')}
                />
                {renderParams(key, cfg)}
              </Box>
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('EinkDesigner.cancel')}</Button>
        <Button variant="contained" onClick={() => onApply(buttons)}>
          {t('EinkDesigner.apply')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Pixel Editor Dialog ──────────────────────────────────────────
function PixelEditorDialog({ open, element, initPixels, onClose, onUpdate }) {
  const [pixels, setPixels] = useState([]);
  const [painting, setPainting] = useState(false);
  const paintValRef = useRef('1');

  useEffect(() => {
    if (!open || !element) return;
    // Use explicitly passed initPixels first (avoids stale element ref)
    if (initPixels?.length) {
      setPixels(initPixels.map(r => r));
    } else if (element.pixels?.length) {
      setPixels(element.pixels.map(r => r));
    } else if (element.type === 'icon') {
      setPixels(iconToPixels(element.name, element.size || 20));
    } else {
      const w = element.width || 60, h = element.height || 60;
      setPixels(Array.from({ length: h }, () => '0'.repeat(w)));
    }
  }, [open, element, initPixels]);

  const togglePixel = useCallback((r, c) => {
    setPixels(prev => {
      const next = [...prev];
      const row = next[r];
      const newVal = paintValRef.current;
      next[r] = row.substring(0, c) + newVal + row.substring(c + 1);
      return next;
    });
  }, []);

  const handleReset = useCallback(() => {
    if (!element) return;
    if (element.type === 'icon') {
      setPixels(iconToPixels(element.name, element.size || 20));
    }
  }, [element]);

  const handleClear = useCallback(() => {
    if (!element) return;
    const pw = element.type === 'icon' ? (element.size || 20) : (element.width || 60);
    const ph = element.type === 'icon' ? (element.size || 20) : (element.height || 60);
    setPixels(Array.from({ length: ph }, () => '0'.repeat(pw)));
  }, [element]);

  const handleInvert = useCallback(() => {
    setPixels(prev => prev.map(row =>
      row.split('').map(c => c === '1' ? '0' : '1').join('')
    ));
  }, []);

  if (!element) return null;

  const pw = pixels[0]?.length || 1;
  const ph = pixels.length || 1;
  const cellSize = Math.max(4, Math.min(12, Math.floor(600 / Math.max(pw, ph))));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg">
      <DialogTitle>
        {`${element.type === 'icon' ? element.name : 'Image'} (${pw}x${ph})`}
      </DialogTitle>
      <DialogContent>
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <Button size="small" variant="outlined" onClick={handleReset}>
            {element.type === 'icon' ? 'Reset' : 'Reset'}
          </Button>
          <Button size="small" variant="outlined" onClick={handleClear}>Clear</Button>
          <Button size="small" variant="outlined" onClick={handleInvert}>Invert</Button>
        </Stack>
        <Box
          sx={{
            display: 'inline-grid',
            gridTemplateColumns: `repeat(${pw}, ${cellSize}px)`,
            border: 1, borderColor: 'divider',
            cursor: 'crosshair',
            userSelect: 'none',
          }}
          onMouseLeave={() => setPainting(false)}
          onMouseUp={() => setPainting(false)}
        >
          {pixels.map((row, r) =>
            row.split('').map((c, col) => (
              <Box
                key={`${r}-${col}`}
                sx={{
                  width: cellSize, height: cellSize,
                  bgcolor: c === '1' ? '#000' : '#fff',
                  border: '0.5px solid',
                  borderColor: 'divider',
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  setPainting(true);
                  paintValRef.current = c === '1' ? '0' : '1';
                  togglePixel(r, col);
                }}
                onMouseEnter={() => {
                  if (painting) togglePixel(r, col);
                }}
              />
            ))
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="success" onClick={() => onUpdate(pixels)}>
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Image B&W Preview Dialog ─────────────────────────────────────
function ImagePreviewDialog({ open, dataUrl, width, height, t, onClose, onAccept }) {
  const [threshold, setThreshold] = useState(128);
  const [contrast, setContrast] = useState(0);
  const [brightness, setBrightness] = useState(0);
  const [dithering, setDithering] = useState(true);
  const [inverted, setInverted] = useState(false);
  const previewRef = useRef(null);
  const imgRef = useRef(null);

  // Load image once when dialog opens
  useEffect(() => {
    if (!open || !dataUrl) return;
    const img = new Image();
    img.onload = () => { imgRef.current = img; };
    img.src = dataUrl;
    // Reset settings
    setThreshold(128);
    setContrast(0);
    setBrightness(0);
    setDithering(true);
    setInverted(false);
  }, [open, dataUrl]);

  // Render preview whenever settings change
  useEffect(() => {
    if (!open || !imgRef.current || !previewRef.current) return;
    const canvas = previewRef.current;
    const ctx = canvas.getContext('2d');
    const imgData = renderBWPreview(imgRef.current, width, height, {
      threshold, contrast, brightness, dithering, inverted,
    });
    canvas.width = width;
    canvas.height = height;
    ctx.putImageData(imgData, 0, 0);
  }, [open, width, height, threshold, contrast, brightness, dithering, inverted]);

  const previewScale = Math.min(3, Math.floor(500 / Math.max(width, height)));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md">
      <DialogTitle>{t('EinkDesigner.image_preview_title') || 'Image B&W Conversion'}</DialogTitle>
      <DialogContent>
        <Stack direction="row" spacing={3} sx={{ pt: 1 }}>
          {/* Preview */}
          <Box sx={{ textAlign: 'center' }}>
            <canvas
              ref={previewRef}
              style={{
                width: width * previewScale,
                height: height * previewScale,
                imageRendering: 'pixelated',
                border: '1px solid #ccc',
              }}
            />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              {width} x {height} px
            </Typography>
          </Box>

          {/* Controls */}
          <Box sx={{ minWidth: 220 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
              {t('EinkDesigner.bw_threshold') || 'Threshold'}
            </Typography>
            <Slider
              value={threshold}
              onChange={(_, v) => setThreshold(v)}
              min={10} max={245} step={1}
              valueLabelDisplay="auto"
              size="small"
            />

            <Typography variant="body2" sx={{ mb: 1, mt: 2, fontWeight: 'bold' }}>
              {t('EinkDesigner.bw_contrast') || 'Contrast'}
            </Typography>
            <Slider
              value={contrast}
              onChange={(_, v) => setContrast(v)}
              min={-100} max={100} step={1}
              valueLabelDisplay="auto"
              size="small"
            />

            <Typography variant="body2" sx={{ mb: 1, mt: 2, fontWeight: 'bold' }}>
              {t('EinkDesigner.bw_brightness') || 'Brightness'}
            </Typography>
            <Slider
              value={brightness}
              onChange={(_, v) => setBrightness(v)}
              min={-100} max={100} step={1}
              valueLabelDisplay="auto"
              size="small"
            />

            <FormControlLabel
              control={<Checkbox checked={dithering} onChange={(e) => setDithering(e.target.checked)} size="small" />}
              label={t('EinkDesigner.bw_dithering') || 'Floyd-Steinberg dithering'}
              sx={{ mt: 1 }}
            />
            <FormControlLabel
              control={<Checkbox checked={inverted} onChange={(e) => setInverted(e.target.checked)} size="small" />}
              label={t('EinkDesigner.inverted') || 'Inverted'}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('EinkDesigner.cancel') || 'Cancel'}</Button>
        <Button
          variant="contained"
          onClick={() => onAccept({ threshold, contrast, brightness, dithering, inverted })}
        >
          {t('EinkDesigner.apply') || 'Apply'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
